/**
 * "Turn this into checks" — the studio's own Notes box, routed to the SAME
 * place a pasted note goes.
 *
 * Why this exists (coach-input refactor PR-9): step 4 of the template wizard
 * already has a coach-notes textarea, and a coach who types "watch for the
 * front knee collapsing inward" there reasonably expects that sentence to be
 * able to become a check. It could not: `coach_notes` is narrative text saved
 * on the template, while the one path that turns words into proposed checks
 * started in a DIFFERENT box in a DIFFERENT step (the Paste Notes tab of the
 * Coach Instruction card).
 *
 * The fix is a join, not a second pipeline. This helper posts the notes to
 * `POST /teaching-videos/paste-notes` and starts processing — byte-for-byte the
 * two calls `TeachingVideoUploader`'s paste tab makes — and returns the new
 * teaching-video id. The page then hands that id to the same
 * `AnnotationSuggestionPanel`, which materialises through the same
 * `materializeSuggestions` call. There is exactly one materialiser
 * (`REFACTOR_PLAN.md` §C.1) and this does not add another.
 *
 * `coach_notes` keeps its narrative role: the text stays in the box and is
 * still saved on the template. Turning it into checks is additive.
 */

import { submitPasteNotes, triggerProcessing } from '@/services/teachingVideoApi';

export interface TurnNotesIntoChecksArgs {
  /** The contents of the coach-notes textarea. */
  text: string;
  sport: string;
  movement: string;
}

/**
 * Submit coach notes for extraction and return the teaching-video id.
 *
 * Throws when the text is empty (the caller should not offer the action in
 * that case) and propagates the server's own coach-facing message for a
 * refusal such as the length cap, so the coach reads the backend's sentence
 * rather than a second one written here.
 */
export async function turnNotesIntoChecks({
  text,
  sport,
  movement,
}: TurnNotesIntoChecksArgs): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error(
      'There is nothing in the notes box yet. Describe what matters in this ' +
        'movement, then try again.',
    );
  }

  const created = await submitPasteNotes(trimmed, sport, movement);
  // Same second call the paste tab makes, so the coach does not need a
  // separate "analyse" click.
  await triggerProcessing(created.id);
  return created.id;
}
